import { useCallback, useState } from 'react';
import { ChevronsUpDown, History, LogOut, UserRound } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { RecentChangesDialog } from '@/components/entry/RecentChangesDialog';
import { ProfileDialog } from '@/components/entry/ProfileDialog';
import { userInitials } from '@/components/entry/user-initials';
import { hasPermission } from '@/lib/permissions';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

export { userInitials };

/**
 * The signed-in user's actions, shared by the sidebar menu (desktop) and the phone "More" sheet:
 * Profile, Recent changes (undo older changes — the entry point that replaced the top-bar Undo button)
 * and Sign out (asks first). Render `dialogs` once next to the trigger.
 */
export function useAccountActions({ user, onSignOut, modules = [] }) {
  const [recentOpen, setRecentOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const confirm = useConfirm();
  const { rememberUntil } = useAuth() || {};
  const isAdmin = ['Admin', 'Super Admin'].includes(String(user?.role || '').trim()) && hasPermission(user, 'admin');

  const signOut = useCallback(async () => {
    const ok = await confirm({
      title: 'Sign out?',
      description: rememberUntil
        ? 'This device stops keeping you signed in. You will need your password (or a passkey) to sign in again here.'
        : 'You will need your username and password to sign in again on this device.',
      confirmLabel: 'Sign out',
      cancelLabel: 'Stay signed in',
      icon: LogOut,
    });
    if (ok) onSignOut?.();
  }, [confirm, onSignOut, rememberUntil]);

  const dialogs = (
    <>
      <RecentChangesDialog open={recentOpen} onOpenChange={setRecentOpen} isAdmin={isAdmin} />
      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} user={user} modules={modules} />
    </>
  );

  return {
    openRecent: () => setRecentOpen(true),
    openProfile: () => setProfileOpen(true),
    signOut,
    dialogs,
  };
}

/**
 * The user block at the bottom of the navy sidebar (02 #8): avatar, name, role and a ⇅ menu with
 * Profile, Recent changes and Sign out. `collapsed` = icon-only sidebar (avatar only).
 * `variant="topbar"` is the light pill the wave-1 top bar used.
 */
export function UserMenu({ user, onLogout, variant = 'sidebar', collapsed = false, modules, account }) {
  // The shell passes its own `account` (shared with the phone sheet, which renders the dialogs).
  const own = useAccountActions({ user, onSignOut: onLogout, modules });
  const { openRecent, openProfile, signOut } = account || own;
  const dialogs = account ? null : own.dialogs;
  const onNavy = variant === 'sidebar';

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex min-w-0 items-center gap-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              onNavy
                ? cn('w-full rounded-control py-2 text-white hover:bg-white/[0.08] data-[state=open]:bg-white/[0.08]', collapsed ? 'justify-center px-0' : 'px-2')
                : 'rounded-full border border-line bg-surface py-1 pl-1 pr-2 shadow-card hover:bg-surface-hover sm:pr-2.5'
            )}
            aria-label={`Account menu for ${user?.name || 'user'}`}
            title={collapsed ? `${user?.name || ''} · ${user?.role || ''}` : undefined}
            data-testid="user-menu-trigger"
          >
            <span
              className={cn(
                'grid h-8 w-8 shrink-0 place-items-center rounded-full text-[12px] font-bold',
                onNavy ? 'bg-white/[0.14] text-white' : 'bg-navy text-white'
              )}
              aria-hidden="true"
            >
              {userInitials(user?.name)}
            </span>
            {!collapsed ? (
              <span className={cn('min-w-0 flex-1', !onNavy && 'hidden sm:block')}>
                <span className={cn('block truncate text-[13px] font-semibold leading-tight', onNavy ? 'text-white' : 'text-ink')}>
                  {user?.name}
                </span>
                <span className={cn('block truncate text-[11.5px] leading-tight', onNavy ? 'text-[color:var(--app-sidebar-muted)]' : 'text-ink-3')}>
                  {user?.role}
                </span>
              </span>
            ) : null}
            {!collapsed ? (
              <ChevronsUpDown className={cn('ml-auto h-4 w-4 shrink-0', onNavy ? 'text-white/60' : 'hidden text-ink-4 sm:block')} aria-hidden="true" />
            ) : null}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={onNavy ? 'start' : 'end'}
          side={onNavy ? 'top' : 'bottom'}
          sideOffset={8}
          className="w-[232px] rounded-control border-line p-1.5 shadow-raised"
          data-testid="user-menu"
        >
          <DropdownMenuLabel className="px-2 py-1.5">
            <span className="block truncate text-[13px] font-semibold text-ink">{user?.name}</span>
            <span className="block truncate text-[12px] font-normal text-ink-3">{user?.role}{user?.email ? ` · ${user.email}` : ''}</span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={openProfile} className="gap-2 text-[13px]" data-testid="user-menu-profile">
            <UserRound className="h-4 w-4 text-ink-3" aria-hidden="true" />
            Profile
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={openRecent} className="gap-2 text-[13px]" data-testid="user-menu-recent-changes">
            <History className="h-4 w-4 text-ink-3" aria-hidden="true" />
            Recent changes
            <span className="ml-auto text-[11.5px] text-ink-3">Undo</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={signOut} className="gap-2 text-[13px]" data-testid="logout-btn">
            <LogOut className="h-4 w-4 text-ink-3" aria-hidden="true" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {dialogs}
    </>
  );
}
